/**
 * Tarayıcıda küçük bir Excel (.xlsx) dosyası üretir (18.10.2026; Ek Mesai Talepleri — liste ucu tek seferde geldiği ve
 * sözleşmede Excel ucu olmadığı için). Kütüphane yok: tek sayfa, ilk satır kalın başlık, metin ve sayı hücreleri;
 * paket sıkıştırmasız ZIP (stored) olarak yazılır.
 */

export type XlsxHucre = string | number | null | undefined;

/** UTF-8 kodlama (test ortamında TextEncoder olmayabilir). */
export const utf8 = (s: string): Uint8Array => {
    var bayt: number[] = [];

    for (var i = 0; i < s.length; i++) {
        var c = s.charCodeAt(i);

        if (c >= 0xd800 && c <= 0xdbff && i + 1 < s.length) {
            var d = s.charCodeAt(i + 1);

            if (d >= 0xdc00 && d <= 0xdfff) {
                c = 0x10000 + ((c - 0xd800) << 10) + (d - 0xdc00);
                i++;
            }
        }

        if (c < 0x80) bayt.push(c);
        else if (c < 0x800) bayt.push(0xc0 | (c >> 6), 0x80 | (c & 63));
        else if (c < 0x10000) bayt.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
        else bayt.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    }

    return new Uint8Array(bayt);
}

let crcTablosu: number[] | undefined;

/** ZIP için CRC-32. */
export const crc32 = (veri: Uint8Array): number => {
    if (!crcTablosu) {
        crcTablosu = [];

        for (var n = 0; n < 256; n++) {
            var c = n;
            for (var k = 0; k < 8; k++)
                c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
            crcTablosu.push(c >>> 0);
        }
    }

    var crc = 0xffffffff;

    for (var i = 0; i < veri.length; i++)
        crc = crcTablosu[(crc ^ veri[i]) & 0xff] ^ (crc >>> 8);

    return (crc ^ 0xffffffff) >>> 0;
}

/** Sıkıştırmasız ZIP paketi. */
export const zipOlustur = (dosyalar: { ad: string, veri: Uint8Array }[]): Uint8Array => {
    var parcalar: Uint8Array[] = [];
    var merkez: Uint8Array[] = [];
    var konum = 0;

    const baslik = (uzunluk: number) => {
        var b = new Uint8Array(uzunluk);
        return { b, v: new DataView(b.buffer) };
    }

    for (var f of dosyalar) {
        var ad = utf8(f.ad);
        var crc = crc32(f.veri);

        var y = baslik(30);
        y.v.setUint32(0, 0x04034b50, true);
        y.v.setUint16(4, 20, true);
        y.v.setUint16(6, 0x0800, true);   // UTF-8 dosya adı
        y.v.setUint16(8, 0, true);        // stored
        y.v.setUint16(10, 0, true);       // saat
        y.v.setUint16(12, 0x21, true);    // tarih: 1980-01-01
        y.v.setUint32(14, crc, true);
        y.v.setUint32(18, f.veri.length, true);
        y.v.setUint32(22, f.veri.length, true);
        y.v.setUint16(26, ad.length, true);
        y.v.setUint16(28, 0, true);
        parcalar.push(y.b, ad, f.veri);

        var m = baslik(46);
        m.v.setUint32(0, 0x02014b50, true);
        m.v.setUint16(4, 20, true);
        m.v.setUint16(6, 20, true);
        m.v.setUint16(8, 0x0800, true);
        m.v.setUint16(10, 0, true);
        m.v.setUint16(12, 0, true);
        m.v.setUint16(14, 0x21, true);
        m.v.setUint32(16, crc, true);
        m.v.setUint32(20, f.veri.length, true);
        m.v.setUint32(24, f.veri.length, true);
        m.v.setUint16(28, ad.length, true);
        m.v.setUint32(42, konum, true);
        merkez.push(m.b, ad);

        konum += 30 + ad.length + f.veri.length;
    }

    var merkezBoyu = merkez.reduce((t, x) => t + x.length, 0);
    var son = baslik(22);
    son.v.setUint32(0, 0x06054b50, true);
    son.v.setUint16(8, dosyalar.length, true);
    son.v.setUint16(10, dosyalar.length, true);
    son.v.setUint32(12, merkezBoyu, true);
    son.v.setUint32(16, konum, true);

    var hepsi = [...parcalar, ...merkez, son.b];
    var sonuc = new Uint8Array(hepsi.reduce((t, x) => t + x.length, 0));
    var i = 0;

    for (var p of hepsi) {
        sonuc.set(p, i);
        i += p.length;
    }

    return sonuc;
}

const xml = (s: string) => s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    // XML 1.0'da geçersiz kontrol karakterleri atılır.
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '');

/** 0 → A, 25 → Z, 26 → AA. */
export const kolonHarfi = (i: number): string => {
    var s = '';
    i++;

    while (i > 0) {
        var m = (i - 1) % 26;
        s = String.fromCharCode(65 + m) + s;
        i = Math.floor((i - 1) / 26);
    }

    return s;
}

/** Excel sayfa adı: en fazla 31 karakter, []:*?/\ olmadan. */
export const sayfaAdi = (s: string) => (s.replace(/[[\]:*?/\\]/g, ' ').trim() || 'Sayfa1').substring(0, 31);

const hucreXml = (h: XlsxHucre, ref: string, stil: number) => {
    if (h == null || h === '')
        return '';

    var s = stil ? ` s="${stil}"` : '';

    if (typeof h == 'number' && isFinite(h))
        return `<c r="${ref}"${s}><v>${h}</v></c>`;

    return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${xml(String(h))}</t></is></c>`;
}

/** Başlık + satırlardan .xlsx baytları. */
export const xlsxOlustur = (ad: string, basliklar: string[], satirlar: XlsxHucre[][]): Uint8Array => {
    var tum: XlsxHucre[][] = [basliklar, ...satirlar];

    var satirXml = tum.map((r, ri) =>
        `<row r="${ri + 1}">${r.map((h, ci) => hucreXml(h, kolonHarfi(ci) + (ri + 1), ri == 0 ? 1 : 0)).join('')}</row>`
    ).join('');

    var genislik = basliklar.map((b, ci) => {
        var enUzun = Math.max(b.length, ...satirlar.map(r => String(r[ci] ?? '').length));
        return `<col min="${ci + 1}" max="${ci + 1}" width="${Math.min(60, Math.max(8, enUzun + 2))}" customWidth="1"/>`;
    }).join('');

    var bas = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>';

    var dosyalar: { ad: string, icerik: string }[] = [
        {
            ad: '[Content_Types].xml',
            icerik: bas + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
                + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
                + '<Default Extension="xml" ContentType="application/xml"/>'
                + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
                + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>'
                + '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>'
                + '</Types>'
        },
        {
            ad: '_rels/.rels',
            icerik: bas + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
                + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
                + '</Relationships>'
        },
        {
            ad: 'xl/workbook.xml',
            icerik: bas + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
                + `<sheets><sheet name="${xml(sayfaAdi(ad))}" sheetId="1" r:id="rId1"/></sheets></workbook>`
        },
        {
            ad: 'xl/_rels/workbook.xml.rels',
            icerik: bas + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
                + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>'
                + '<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>'
                + '</Relationships>'
        },
        {
            ad: 'xl/styles.xml',
            icerik: bas + '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
                + '<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts>'
                + '<fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills>'
                + '<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>'
                + '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>'
                + '<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs>'
                + '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>'
                + '</styleSheet>'
        },
        {
            ad: 'xl/worksheets/sheet1.xml',
            icerik: bas + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
                + '<sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>'
                + (genislik ? `<cols>${genislik}</cols>` : '')
                + `<sheetData>${satirXml}</sheetData></worksheet>`
        },
    ];

    return zipOlustur(dosyalar.map(f => ({ ad: f.ad, veri: utf8(f.icerik) })));
}

/** Dosyayı tarayıcıda indirir. */
export const xlsxIndir = (dosyaAdi: string, ad: string, basliklar: string[], satirlar: XlsxHucre[][]) => {
    var bayt = xlsxOlustur(ad, basliklar, satirlar);
    var blob = new Blob([bayt], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    var url = URL.createObjectURL(blob);
    var a = document.createElement('a');

    a.href = url;
    a.download = dosyaAdi.endsWith('.xlsx') ? dosyaAdi : dosyaAdi + '.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
